import React, { KeyboardEvent, useCallback, useEffect, useState } from 'react'
import { API } from '../../../../api'
import { Preloader } from '../../../common'
import { colors } from '@mui/material'
import CardContent from '@mui/material/CardContent'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Box from '@mui/material/Box'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import { HelpOutline, SearchOutlined } from '@mui/icons-material'
import { getADConstants, ADManagementConstants } from '../../../../store/reducer/ad-management'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { IADGroup } from '../'
import { MembershipGroupNameDialogButton, MembershipGroupNameDialogView } from '../membership'
import { useGroupSearchable } from '../../../../hooks'
import { EntitiesTypes, getEntityTitle } from '../../../../view/admin/ad-management/EntitiesTabs'
import { boxName } from './EntityGroupsAdded'

interface IProps {
  groupsSelected: IADGroup[]
  onGroup: (group: IADGroup) => void
  onSearch: () => void
  type: EntitiesTypes
}
export const EntityGroupsAll = ({ groupsSelected, onGroup, onSearch, type }: IProps) => {
  const titleName = getEntityTitle(type)
  const dispatch = useAppDispatch()
  const ADConstant = useAppSelector(ADManagementConstants)

  const getToolTip = () => {
    switch (type) {
      case EntitiesTypes.pharmacies:
        return ADConstant.pharmacy_groups_location
      case EntitiesTypes.distributors:
        return ADConstant.distributor_groups_location
      case EntitiesTypes.manufacturers:
        return ADConstant.manufacturer_groups_location
      case EntitiesTypes.referral_types:
        return ADConstant.referral_types_groups_location
      case EntitiesTypes.omit_measures:
        return ADConstant.omit_groups_location
      case EntitiesTypes.reports:
        return ADConstant.report_groups_location
      default:
        return ''
    }
  }

  const handleScroll = (event: React.UIEvent<HTMLElement>) => {
    event.preventDefault()
    const target = event.target as HTMLElement
    if (target.offsetHeight + target.scrollTop + 1 > target.scrollHeight) {
      if (page < (totalPages as number) && !isLoadingMore) {
        setPage(page + 1)
      }
    }
  }

  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [totalPages, setTotalPages] = useState(0)
  const [groups, setGroups] = useState<IADGroup[]>([])
  const { searchableText, setSearchableText, isShow, groupDialogDetailTitle, onCancel, setGroupDialogDetailTitle } = useGroupSearchable()

  const callADConstants = useCallback(async () => {
    if (!getToolTip()) {
      dispatch(getADConstants())
    }
  }, [dispatch, ADConstant])

  useEffect(() => {
    void callADConstants()
  }, [callADConstants])

  const getCoveredEntityGroups = useCallback(async () => {
    try {
      setIsLoading(true)
      setIsLoadingMore(true)
      let apiCall
      switch (type) {
        case EntitiesTypes.pharmacies:
          apiCall = API.ADManagement.group.pharmacy_groups
          break
        case EntitiesTypes.distributors:
          apiCall = API.ADManagement.group.distributor_groups
          break
        case EntitiesTypes.manufacturers:
          apiCall = API.ADManagement.group.manufacturers
          break
        case EntitiesTypes.referral_types:
          apiCall = API.ADManagement.group.referral_types_groups
          break
        case EntitiesTypes.omit_measures:
          apiCall = API.ADManagement.group.omit_groups
          break
        case EntitiesTypes.reports:
          apiCall = API.ADManagement.group.report_groups
          break
        default:
          setGroups([])
          setIsLoading(false)
          return
      }

      const { results, page_number, total_pages } = await apiCall(search, page)

      if (results) {
        setGroups((groups) => [...groups, ...results.map(({ GroupName }) => ({ name: GroupName, isChecked: false }))])

        setPage(page_number as number)
        setTotalPages(total_pages as number)
      } else {
        setGroups([])
      }
    } finally {
      setIsLoadingMore(false)
      setIsLoading(false)
    }
  }, [type, page, search])

  useEffect(() => {
    void getCoveredEntityGroups()
  }, [getCoveredEntityGroups])

  const renderPreloader = () => (
    <Preloader
      size='sm'
      style={{
        display: 'block',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}
    />
  )

  const renderHeader = () => {
    const renderSearch = () => {
      const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          const newValue = (event.target as HTMLInputElement).value
          if (search !== newValue) {
            setGroups([])
            setSearch(newValue)
            onSearch()
            setPage(1)
          }
        }
      }

      return (
        <TextField
          size='small'
          fullWidth
          placeholder='Search'
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchOutlined />
              </InputAdornment>
            ),
          }}
          inputProps={{
            'data-testid': 'groups-all-search',
          }}
          margin='none'
          onKeyDown={handleKeyDown}
        />
      )
    }

    return (
      <Grid
        container
        alignItems='center'
        sx={{ mb: 2 }}
      >
        <Grid
          item
          xs={7}
          sx={{ position: 'relative', pl: 4 }}
        >
          <Tooltip
            arrow
            title={getToolTip()}
          >
            <HelpOutline sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', color: 'var(--pantoneBlue)' }} />
          </Tooltip>
          <Typography
            variant='h3'
            sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', pr: 1 }}
          >
            {boxName(type, titleName)} Groups
          </Typography>
        </Grid>
        <Grid
          item
          xs={5}
        >
          {renderSearch()}
        </Grid>
        <MembershipGroupNameDialogView
          isShow={isShow}
          searchableText={searchableText}
          setSearchableText={setSearchableText}
          onCancel={onCancel}
          setTitle={setGroupDialogDetailTitle}
          title={groupDialogDetailTitle}
          from='entities'
        />
      </Grid>
    )
  }

  const renderGroups = () => {
    const isSelected = (n: string) => groupsSelected.findIndex(({ name }) => name === n) !== -1

    const defineColor = (name: string) => (isSelected(name) ? colors.green[500] : colors.grey[800])

    const handleGroup = (group: IADGroup, isChecked: boolean) => {
      group.isChecked = isChecked
      onGroup(group)
    }

    return (
      <FormControl
        fullWidth
        sx={{
          height: 'calc(100% - 90px)',
          overflowY: 'auto',
        }}
        onScroll={handleScroll}
      >
        <FormGroup>
          {groups.map((group, index) => (
            <Card
              key={index}
              variant='outlined'
              sx={{ mb: 2 }}
            >
              <FormControlLabel
                labelPlacement='start'
                control={
                  <Checkbox
                    checked={group.isChecked}
                    disabled={isSelected(group.name)}
                    onChange={({ target: { checked } }) => handleGroup(group, checked)}
                  />
                }
                label={
                  <MembershipGroupNameDialogButton
                    label={group.name}
                    disabled={isSelected(group.name)}
                    setSearchableText={setSearchableText}
                    searchableText={group.name}
                  />
                }
                sx={{
                  py: 1,
                  pl: 2,
                  pr: 1,
                  mx: 0,
                  width: '100%',
                  justifyContent: 'space-between',
                  borderLeft: `3px solid ${defineColor(group.name)}`,
                }}
                data-testid={group.name}
              />
            </Card>
          ))}
          {isLoadingMore ? (
            <Preloader
              size='xs'
              style={{ margin: '0 auto' }}
            />
          ) : (
            <></>
          )}
        </FormGroup>
      </FormControl>
    )
  }

  const renderEmptyState = () => <Box sx={{ textAlign: 'center', mt: 2, fontWeight: 'bold', color: 'var(--pantoneBlue)' }}>No Results</Box>

  return (
    <Card data-testid='groups-all'>
      <CardContent sx={{ position: 'relative', height: 'calc(100vh - 400px)' }}>
        {renderHeader()}
        {isLoading && page === 1 ? renderPreloader() : <>{groups.length > 0 ? <>{renderGroups()}</> : renderEmptyState()}</>}
      </CardContent>
    </Card>
  )
}
